<template>
    <!-- Progressive disclosure inside the locked offers card: skeleton rows
         with one Unlock button until tapped, then the two capture fields in
         the same spot. Inline on purpose - the previous modal was a
         position:fixed overlay, and every problem this flow ever had (the iOS
         caret painting outside the field, the keyboard shoving the modal
         around) was overlay pathology. An in-flow form scrolls natively. -->
    <div v-if="!expanded" class="relative">
        <ul class="space-y-4 blur-[2px] select-none pointer-events-none" aria-hidden="true">
            <li
                v-for="i in skeletonCount"
                :key="i"
                class="flex items-center justify-between gap-6"
            >
                <span class="h-3.5 rounded-full bg-brand-2-950/15" :class="i === 2 ? 'w-44' : 'w-36'"></span>
                <span class="h-3.5 w-20 rounded-full bg-brand-2-950/15"></span>
            </li>
        </ul>

        <div class="absolute inset-0 flex items-center justify-center">
            <button
                class="button button-2 button-small"
                @click.prevent="expand"
            >
                Unlock with your email
            </button>
        </div>
    </div>

    <!-- Post-subscribe: the offers revealed in place, no reload. The endpoint
         returns them with the success response, so the subscriber sees what
         they unlocked exactly where they were - a reload's scroll restoration
         used to drop them at an arbitrary point on the page. -->
    <div
        v-else-if="offers"
        class="space-y-6"
    >
        <!-- Symmetric pt/pb, mirroring the Twig unlocked list exactly: v4's
             divide-y draws each border on the row's bottom, so padding only
             the top left every line hugging the row above and the final
             borderless offer adrift. -->
        <ul class="[&_strong]:text-brand-2-950 leading-none divide-y divide-brand-2-950/10 [&>li:not(:first-child)]:pt-4 [&>li:not(:last-child)]:pb-4">
            <li
                v-for="offer in offers"
                :key="offer.dateRange"
                class="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1.5"
            >
                <strong>{{ offer.dateRange }}</strong>
                <span class="flex gap-2.5 tabular-nums">
                    <strong>Price</strong>
                    {{ offer.price }}
                </span>
            </li>
        </ul>

        <a
            href="#enquiry-form"
            class="button button-2 button-small"
        >
            Enquire about these dates
        </a>
    </div>

    <form
        v-else
        class="space-y-4"
        @submit.prevent="submitForm"
    >
        <FormError :message="errorMessage" />

        <div class="form-col">
            <label
                for="offers-name"
                class="label"
            >
                Full name
            </label>
            <input
                id="offers-name"
                ref="nameField"
                v-model="form.name"
                class="input"
                type="text"
                required
            >
        </div>

        <div class="form-col">
            <label
                for="offers-email"
                class="label"
            >
                Email address
            </label>
            <input
                id="offers-email"
                v-model="form.email"
                class="input"
                type="email"
                required
            >
        </div>

        <div>
            <button
                class="button button-2 button-small"
                :disabled="submitting"
            >
                Subscribe

                <span
                    v-if="submitting"
                    class="size-4 text-white"
                    v-html="SpinnerIcon"
                ></span>
            </button>
        </div>
    </form>
</template>

<script setup>
    import { ref, computed, nextTick } from 'vue';
    import axios from 'axios';

    import FormError from '@js/src/components/FormError.vue';

    import SpinnerIcon from '@img/icons/spinner.svg?raw';
    import { setCookie } from '@js/src/cookies';
    import { pushToDataLayer } from '@js/src/dataLayer';

    const props = defineProps([
        'propertyId',
        'count',
    ]);

    // Fired on successful subscribe so the Twig-rendered locked-state copy
    // around this component ("N subscriber-only offers", "Free to join")
    // can hide - the in-place reveal can't reach markup it doesn't own.
    const emit = defineEmits(['revealed']);

    const expanded = ref(false);
    const offers = ref(null);
    const submitting = ref(false);
    const errorMessage = ref('');
    const nameField = ref(null);

    const form = ref({
        name: '',
        email: '',
        // Consent is the act of submitting a form labelled Subscribe, under the
        // card's own "Free to join - we'll email you occasional special offers"
        // line; the endpoint requires the flag.
        newsletter_subscribe: true,
    });

    // Enough rows to show the shape of what unlocking reveals, without
    // promising a length.
    const skeletonCount = computed(() => Math.min(parseInt(props.count, 10) || 1, 3));

    // Same event, same stages as the modal it replaces, so the GA4 funnel is
    // comparable across the change: opened = the unlock tap.
    function pushSubscribeEvent(stage) {
        pushToDataLayer({
            event: 'special_offers_subscribe',
            subscribe_stage: stage,
            subscribe_property: props.propertyId,
        }, { immediate: stage !== 'opened' });
    }

    function expand() {
        expanded.value = true;

        pushSubscribeEvent('opened');

        nextTick(() => nameField.value?.focus());
    }

    function submitForm() {
        errorMessage.value = '';
        submitting.value = true;

        axios.post(`/wp-json/lh/v1/properties/${props.propertyId}/special-offers/subscribe`, form.value, {
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': Page.nonce,
            },
        })
            .then(response => {
                pushSubscribeEvent('subscribed');

                // The cookie makes the NEXT page load render unlocked
                // server-side; this visit reveals in place from the response.
                setCookie('special_offers_subscribed', true, 365);
                offers.value = response.data.offers || [];

                emit('revealed');

                // Filling the form left the page scrolled to wherever the
                // keyboard pushed it, so the reveal lands mid-list with the
                // heading and the owner's promo line off-screen above. Bring
                // the card's top back into view (its scroll-mt clears the
                // fixed header) so the subscriber sees the whole section.
                nextTick(() => {
                    document.getElementById('special-offers')?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                });
            })
            .catch(error => {
                const responseErrors = error?.response?.data?.data?.errors || {};
                const firstError = Object.values(responseErrors)[0] || '';

                errorMessage.value = Object.values(firstError)[0]
                    ? Object.values(firstError)[0]
                    : (error?.response?.data?.message || 'Sorry, an error occurred.');
            })
            .finally(() => {
                submitting.value = false;
            });
    }
</script>
